import { i18next } from '@hotjar/translations';
import { tokens } from '@hotjar/hotsauce';
import { invalidateRecentSurveysCache } from '@hotjar/webapp/hooks/useRecentSurveys';
import {
    getWidgetConfiguration,
    setWidgetConfiguration,
    updateWidgetConfiguration
} from '@hotjar/integrations/domains/slack/api';
import * as SlackFeature from '@hotjar/integrations/domains/slack/feature';
import * as MSTeamsFeature from '@hotjar/integrations/domains/ms-teams/feature';
import * as WebhooksFeature from '@hotjar/integrations/domains/webhooks/feature';
import {
    getSurveySettings as getMsTeamsSurveySettings,
    saveSurveySettings as saveMsTeamsSurveySettings
} from '@hotjar/integrations/domains/ms-teams/api';

import {
    getSurveySettings as getWebhooksSurveySettings,
    saveSurveySettings as saveWebhooksSurveySettings
} from '@hotjar/integrations/domains/webhooks/api';
import { isModal, isOffsite, isPopover } from '@hotjar/feedback/domain/Surveys/Survey/SurveyLocations';
import {
    getSurveyTemplateById,
    getSurveyTemplateId,
    setSurveyTemplateIdToAppState
} from '@hotjar/webapp/features/Surveys/Templates';
import * as gettingStartedGuide from '@hotjar/webapp/domains/activation/getting-started-guide';
import * as CONSTANTS from '@hotjar/insights-common/constants/legacy';
import {
    invalidateFeedbackToolsCount,
    invalidateSurveyWidgets,
    invalidateSurveyQuestions,
    saveWizardState,
    loadWizardState,
    clearWizardState,
    BACKGROUND_COLORS,
    BUTTON_COLORS,
    FONT_COLORS,
    ADVANCED_BACKGROUND_COLORS,
    ADVANCED_BUTTON_COLORS,
    LEGACY_BACKGROUND_COLORS,
    LEGACY_BUTTON_COLORS,
    SECTIONS,
    QUESTION_TYPES
} from '@hotjar/feedback';
import { SURVEYS } from '@hotjar/webapp/constants/routes';
import { scheduleTrigger } from '@hotjar/feedback';
import { getPollDefaultData } from '@hotjar/common';
import { cloneDeep } from 'lodash-es';
import { v4 as uuid } from 'uuid';

function isCustomBackground(color) {
    return !BACKGROUND_COLORS.map((c) => c.fill).includes(color);
}

function isCustomButtonColor(color) {
    return !BUTTON_COLORS.map((c) => c.fill).includes(color);
}

let initialized = false;
let canSave = false;

angular
    .module('hjSurveysModule')
    .controller(
        'PollsEditCreateCtrl',
        function (
            $interval,
            $modal,
            $rootScope,
            $q,
            $scope,
            $state,
            $stateParams,
            $timeout,
            $window,
            ColorHelper,
            Allowances,
            AskFeature,
            NextItemData,
            Notify,
            Plan,
            PollModel,
            Restangular,
            restDefaultHandlers,
            Site,
            TargetingRule,
            Track,
            UiHelper,
            User,
            UserData,
            ReactNavigation
        ) {
            /*************************
             * PRIVATE VARIABLES
             *************************/
            const rSite = Restangular.one('sites', $stateParams.siteId);
            const rPolls = rSite.all('polls');
            // todo: update in RXP-1580 once RXP-1577 is merged
            const rToolsCount = rSite.one('tools-count');
            const rUserAttributes = rSite.one('attributes');
            let rPoll;
            const user = User.get();
            let saveInterval;

            const defaultSurveyData = Object.freeze({ ...getPollDefaultData(true) });
            /*************************
             * SCOPE VARIABLES
             *************************/
            $scope.model = {
                flowSections: {
                    name: {
                        name: SECTIONS.name,
                        nextSection: SECTIONS.location,
                        invalidMessage: 'Give your Survey a name',
                        success: null,
                        validate: validateNameSection
                    },
                    location: {
                        name: SECTIONS.location,
                        nextSection: SECTIONS.questions,
                        invalidMessage: 'Set the location for your Survey to appear',
                        success: null,
                        validate: validateLocationSection
                    },
                    questions: {
                        name: SECTIONS.questions,
                        nextSection: SECTIONS.appearance,
                        invalidMessage: 'Define the questions you would like to ask your users',
                        success: null,
                        validate: validateQuestionsSection
                    },
                    appearance: {
                        name: SECTIONS.appearance,
                        nextSection: SECTIONS.targeting,
                        invalidMessage: 'Set the language and appearance of your Survey',
                        success: null,
                        validate: validateAppearanceSection,
                        fallbackToDefaultData: () => {
                            $scope.model.currentPoll.background = defaultSurveyData.background;
                            $scope.model.currentPoll.button_color = defaultSurveyData.button_color;
                            $scope.model.currentPoll.position = defaultSurveyData.position;
                            $scope.model.currentPoll.skin = defaultSurveyData.skin;
                        }
                    },
                    invitation: {
                        name: SECTIONS.invitation,
                        nextSection: SECTIONS.forwarding,
                        invalidMessage: 'Decide if you invite people on your platform',
                        success: null,
                        validate: validateInvitationSection,
                        fallbackToDefaultData: () => {
                            $scope.model.currentPoll.invite_enabled = defaultSurveyData.invite_enabled;
                        }
                    },
                    targeting: {
                        name: SECTIONS.targeting,
                        nextSection: SECTIONS.behavior,
                        invalidMessage: 'Set on which devices and pages the Survey should show',
                        success: null,
                        validate: validateTargetingSection,
                        fallbackToDefaultData: () => {
                            $scope.onTargetingChange([
                                {
                                    id: uuid(),
                                    error: undefined,
                                    target: { component: 'url', pattern: '/', match_operation: 'contains ' }
                                }
                            ]);

                            const defaultTargeting = defaultSurveyData.targeting;
                            defaultTargeting.push(angular.copy(TargetingRule.ruleObject.url.allPages));

                            $scope.model.currentPoll.targeting = defaultTargeting;
                            $scope.model.currentPoll.targeting_percentage = defaultSurveyData.targeting_percentage;
                        }
                    },
                    behavior: {
                        name: SECTIONS.behavior,
                        nextSection: SECTIONS.forwarding,
                        invalidMessage: "Set the Survey's behavior",
                        success: null,
                        validate: validateBehaviorSection,
                        fallbackToDefaultData: () => {
                            $scope.model.currentPoll.display_condition = defaultSurveyData.display_condition;
                            $scope.model.currentPoll.display_delay = defaultSurveyData.display_delay;
                            $scope.model.currentPoll.persist_condition = defaultSurveyData.persist_condition;
                        }
                    },
                    forwarding: {
                        name: SECTIONS.forwarding,
                        nextSection: SECTIONS.review,
                        invalidMessage: 'Set if responses should be forwarded',
                        success: null,
                        validate: validateForwardingSection,
                        getActiveForwardingNames: getActiveForwardingNames
                    },
                    review: {
                        name: SECTIONS.review,
                        success: true
                    }
                },
                currentPoll: null,
                loaded: false,
                mode: $stateParams.pollId ? 'edit' : 'create',
                planName: Plan.get().effective.name,
                previewConfig: {},
                skinChoices: CONSTANTS.skinChoices,
                sectionOptions: {
                    activeQuestionTab: 'preview',
                    bankFilter: '',
                    emailForwardingEnabled: true
                },
                pollsCount: null,
                userAttributes: [],
                dataFromTemplate: $stateParams.dataFromTemplate,
                templateId: $stateParams.templateId,
                forwarding_email_address_list: null,
                forwardingSettings: {},
                siteId: $stateParams.siteId,
                pollId: $stateParams.pollId || null,
                activeSection: 'name',
                openedSections: {},
                hiddenSections: {
                    questions: true,
                    appearance: true,
                    invitation: true,
                    targeting: true,
                    behavior: true,
                    forwarding: true,
                    review: true
                }
            };
            $scope.model.featureLimits = AskFeature.getLimitsForAsk('polls', $scope.model.pollsCount);
            $scope.defaultBackgroundColor = BACKGROUND_COLORS[0].fill;
            $scope.defaultButtonColor = BUTTON_COLORS[0].fill;
            $scope.defaultFontColor = FONT_COLORS[0].fill;
            $scope.baseBackgroundColors = BACKGROUND_COLORS;
            $scope.advancedBackgroundColors = ADVANCED_BACKGROUND_COLORS;
            $scope.additionalBackgroundColor = null;
            $scope.baseButtonColors = BUTTON_COLORS;
            $scope.advancedButtonColors = ADVANCED_BUTTON_COLORS;
            $scope.additionalButtonColor = null;
            $scope.fontColors = FONT_COLORS;
            $scope.isSaving = false;
            $scope.form = { $dirty: false };
            /*************************
             * ACCESS FUNCTIONS
             * These should remain functions, painful as it may be, so that on upsell they get re-checked in a digest().
             *************************/
            $scope.canUseTargetingCapFeature = function () {
                return AskFeature.isAllowedForAsk('targeting.cap');
            };

            $scope.canUseUserAttributes = function () {
                return AskFeature.isAllowedForAsk('user_attributes.toggle');
            };

            $scope.canUseSlackIntegration = function () {
                return AskFeature.isAllowedForAsk(SlackFeature.FEATURE_NAME);
            };
            $scope.canUseMsTeamsIntegration = async function () {
                // Site is in the minimal plan (business)
                return await Allowances.isGateOpen(MSTeamsFeature.GATE_FEEDBACK, {
                    id: $stateParams.siteId,
                    type: 'site'
                });
            };

            $scope.canUseWebhooksIntegration = async function () {
                // Site is in the minimal plan (scale) and FF allowed
                const isGateOpen = await Allowances.isGateOpen(WebhooksFeature.GATE_FEEDBACK, {
                    id: $stateParams.siteId,
                    type: 'site'
                });

                const isAllowed = AskFeature.isAllowedForAsk(WebhooksFeature.FEATURE_NAME);

                return isGateOpen && isAllowed;
            };

            /*************************
             * INITIALIZATION
             *************************/
            this.$onInit = function () {
                return checkIfTemplateAvailable().then(() => {
                    if ($scope.model.mode === 'create') {
                        const toolCreatedFrom = $stateParams.copyId ? 'duplicate' : 'new';
                        const customPollData = NextItemData.get('poll');
                        let data = {};

                        if (customPollData && customPollData.originator) {
                            data = {
                                state_event_was_triggered_on: customPollData.originator.state,
                                url_event_was_triggered_on: customPollData.originator.url
                            };
                        }

                        data.poll_template_name = $scope.model.templateData?.name || 'not-set';
                        data.poll_template_category = $scope.model.templateCategoryTitle || 'not-set';
                        Track.toolCreationStarted('Poll', toolCreatedFrom, data);
                    }

                    if ($scope.model.mode === 'create' && $scope.model.featureLimits.applies) {
                        rToolsCount.get({ fields: 'n_polls' }).then(
                            function (res) {
                                $scope.model.pollsCount = res.n_polls;
                                $scope.model.featureLimits = AskFeature.getLimitsForAsk(
                                    'polls',
                                    $scope.model.pollsCount
                                );
                                if ($scope.model.featureLimits.reached) {
                                    setNewPollData();
                                    $scope.model.loaded = true;
                                } else {
                                    loadPoll();
                                }
                            },
                            function (res) {
                                restDefaultHandlers.onError(res);
                            }
                        );
                    } else {
                        loadPoll();
                    }

                    if ($scope.canUseUserAttributes()) {
                        rUserAttributes.get().then(function (res) {
                            $scope.model.userAttributes = res.attributes;
                        }, restDefaultHandlers.onError);
                    }
                });
            };

            /*************************
             * PRIVATE FUNCTIONS
             *************************/

            // Get a string with the name of the active forwarding methods:
            // e.g. Slack, Microsoft Teams, Email
            function getActiveForwardingNames() {
                const forwarding = {
                    slack: 'Slack',
                    msTeams: 'Microsoft Teams',
                    email: 'Email',
                    webhooks: 'Webhooks'
                };

                return Object.keys(forwarding)
                    .filter((key) => $scope.enabledAndValid($scope.model.forwardingSettings[key]))
                    .map((key) => forwarding[key])
                    .join(', ');
            }

            function initForwardingQuestions(poll) {
                const allQuestions = poll.content.questions.map(({ uuid, text }) => ({
                    value: uuid,
                    label: text
                }));
                $scope.model.forwardingSettings.common = {
                    allQuestions
                };
            }

            function initSlackForwardingQuestions(poll, { questions: savedQuestions } = {}) {
                const { slack } = $scope.model.forwardingSettings;
                slack.selectedQuestions = savedQuestions
                    ? savedQuestions
                    : poll.content.questions.map(({ uuid }) => uuid);
            }

            function initMsTeamsForwardingQuestions(poll, { questions: savedQuestions } = {}) {
                const { msTeams } = $scope.model.forwardingSettings;
                msTeams.selectedQuestions = savedQuestions
                    ? savedQuestions
                    : poll.content.questions.map(({ uuid }) => uuid);
            }

            function addForwardingQuestion(index) {
                const { questions } = $scope.model.currentPoll.content;
                const { slack, msTeams, common } = $scope.model.forwardingSettings;
                const addedIndex = typeof index === 'undefined' ? common.allQuestions.length : index;
                const addedQuestion = questions[addedIndex];

                common.allQuestions = [
                    ...common.allQuestions.slice(0, addedIndex),
                    { value: addedQuestion.uuid, label: addedQuestion.text },
                    ...common.allQuestions.slice(addedIndex)
                ];
                slack.selectedQuestions = [...slack.selectedQuestions, addedQuestion.uuid];
                msTeams.selectedQuestions = [...msTeams.selectedQuestions, addedQuestion.uuid];
            }

            function removeForwardingQuestions(removedQuestion) {
                const { slack, msTeams, common } = $scope.model.forwardingSettings;
                common.allQuestions = common.allQuestions.filter(({ value }) => value !== removedQuestion.uuid);
                [slack.selectedQuestions, msTeams.selectedQuestions].forEach((selectedQuestions) => {
                    selectedQuestions = selectedQuestions.filter((uuid) => uuid !== removedQuestion.uuid);
                    if (selectedQuestions.length === 0) {
                        selectedQuestions = [common.allQuestions[0].value];
                    }
                });
            }

            function updateForwardingQuestions(updatedQuestion) {
                const { common } = $scope.model.forwardingSettings;
                common.allQuestions = common.allQuestions.map((question) =>
                    question.value === updatedQuestion.uuid && question.label !== updatedQuestion.text
                        ? { ...question, label: updatedQuestion.text }
                        : question
                );
            }

            function reloadForwardingQuestions() {
                const { common } = $scope.model.forwardingSettings;
                const { questions } = $scope.model.currentPoll.content;

                common.allQuestions = questions.map(({ uuid, text }) => ({
                    value: uuid,
                    label: text
                }));
            }

            function initSlackForwardingForExistingPoll(poll) {
                if ($scope.canUseSlackIntegration() && poll?.id) {
                    getWidgetConfiguration($stateParams.siteId, 'poll', poll.id)
                        .then(({ data }) => {
                            $timeout(() => {
                                $scope.model.forwardingSettings.slack = {
                                    channels: data.channels || [],
                                    enabled: data.enabled,
                                    valid: true,
                                    dirty: false,
                                    isConnected: false
                                };
                                initSlackForwardingQuestions(poll, data.settings || {});
                            });
                        })
                        .catch(() => {
                            $timeout(() => {
                                $scope.model.forwardingSettings.slack = {
                                    enabled: false,
                                    channels: [],
                                    valid: true,
                                    dirty: false,
                                    isConnected: false
                                };
                                initSlackForwardingQuestions(poll);
                            });
                        });
                } else {
                    $scope.model.forwardingSettings.slack = {
                        enabled: false,
                        channels: [],
                        valid: true,
                        dirty: false,
                        isConnected: false,
                        selectedQuestions: []
                    };
                    initSlackForwardingQuestions(poll);
                }
            }

            async function initMSTeamsForwardingForExistingPoll(poll) {
                const baseMsTeamsSettings = {
                    enabled: false,
                    webhooks: [],
                    selectedQuestions: [],
                    connected: false,
                    valid: false,
                    dirty: false
                };
                if (poll?.id) {
                    $scope.canUseMsTeamsIntegration().then((canUse) => {
                        if (!canUse) {
                            // Site not in minimal plan
                            $scope.model.forwardingSettings.msTeams = {
                                ...baseMsTeamsSettings
                            };
                            return;
                        }
                        // Feature available for the site
                        getMsTeamsSurveySettings($stateParams.siteId, poll.id)
                            .then((data) => {
                                $timeout(() => {
                                    $scope.model.forwardingSettings.msTeams = {
                                        ...baseMsTeamsSettings,
                                        enabled: data.enabled,
                                        webhooks: data.webhooks || [],
                                        valid: data.webhooks?.length > 0
                                    };
                                    initMsTeamsForwardingQuestions(poll, data.settings || {});
                                });
                            })
                            .catch(() => {
                                $timeout(() => {
                                    $scope.model.forwardingSettings.msTeams = {
                                        ...baseMsTeamsSettings
                                    };
                                    initMsTeamsForwardingQuestions(poll);
                                });
                            });
                    });
                } else {
                    $scope.model.forwardingSettings.msTeams = {
                        ...baseMsTeamsSettings
                    };
                    initMsTeamsForwardingQuestions(poll);
                }
            }

            async function initWebhooksForwardingForExistingPoll(poll) {
                const baseWebhooksSettings = {
                    enabled: false,
                    webhooks: [],
                    valid: false,
                    dirty: false
                };

                if (!poll?.id) {
                    $scope.model.forwardingSettings.webhooks = baseWebhooksSettings;
                    return;
                }

                $scope.canUseWebhooksIntegration().then((canUse) => {
                    if (!canUse) {
                        // Site not in minimal plan
                        $scope.model.forwardingSettings.webhooks = baseWebhooksSettings;
                        return;
                    }

                    // Feature available for the site
                    getWebhooksSurveySettings($stateParams.siteId, poll.id)
                        .then((data) => {
                            $timeout(() => {
                                $scope.model.forwardingSettings.webhooks = {
                                    ...baseWebhooksSettings,
                                    enabled: data.enabled,
                                    webhooks: data.webhooks || [],
                                    valid: data.webhooks?.length > 0
                                };
                            });
                        })
                        .catch(() => {
                            $timeout(() => {
                                $scope.model.forwardingSettings.webhooks = baseWebhooksSettings;
                            });
                        });
                });
            }

            function initForwardingForNewPoll() {
                $scope.model.forwardingSettings.common = {
                    allQuestions: []
                };
                $scope.model.forwardingSettings.slack = {
                    enabled: false,
                    channels: [],
                    valid: true,
                    dirty: false,
                    isConnected: false,
                    selectedQuestions: []
                };
                $scope.model.forwardingSettings.email = {
                    enabled: false,
                    emails: [],
                    valid: true,
                    dirty: false
                };
                $scope.model.forwardingSettings.msTeams = {
                    enabled: false,
                    webhooks: [],
                    selectedQuestions: [],
                    connected: false,
                    valid: false,
                    dirty: false
                };

                $scope.model.forwardingSettings.webhooks = {
                    enabled: false,
                    webhooks: [],
                    valid: false,
                    dirty: false
                };
            }

            function initIntegrationForwardingForExistingPoll(poll) {
                initForwardingQuestions(poll);
                initSlackForwardingForExistingPoll(poll);
                initMSTeamsForwardingForExistingPoll(poll);
                initWebhooksForwardingForExistingPoll(poll);
            }

            function initEmailForwardingForExistingPoll(poll) {
                $scope.model.forwardingSettings.email = {
                    emails: poll.forwarding_email_address_list,
                    enabled: poll.forwarding_email_address_list.length > 0,
                    valid: true,
                    dirty: false
                };
            }

            function resetForwardingSettings() {
                $scope.model.forwardingSettings.email.dirty = false;
                $scope.model.forwardingSettings.slack.dirty = false;
                $scope.model.forwardingSettings.msTeams.dirty = false;
            }

            function setForwardingDirty() {
                if ($scope.model.forwardingSettings.slack) {
                    // We have to create a new object otherwise the `ForwardToSlack` component won't re-render
                    $scope.model.forwardingSettings.slack = {
                        ...$scope.model.forwardingSettings.slack,
                        dirty: $scope.model.forwardingSettings.slack.enabled
                    };
                }

                if ($scope.model.forwardingSettings.email) {
                    // We have to create a new object otherwise the `ForwardToEmail` component won't re-render
                    $scope.model.forwardingSettings.email = {
                        ...$scope.model.forwardingSettings.email,
                        dirty: $scope.model.forwardingSettings.email.enabled
                    };
                }

                if ($scope.model.forwardingSettings.msTeams) {
                    // We have to create a new object otherwise the `ForwardToMsTeams` component won't re-render
                    $scope.model.forwardingSettings.msTeams = {
                        ...$scope.model.forwardingSettings.msTeams,
                        dirty: $scope.model.forwardingSettings.msTeams.enabled
                    };
                }
            }

            function saveEmailForwardingSettings(currentPoll) {
                if ($scope.model.forwardingSettings.email.enabled) {
                    currentPoll.forwarding_email_address_list = _.uniq($scope.model.forwardingSettings.email.emails);
                } else {
                    currentPoll.forwarding_email_address_list = [];
                }
            }

            function saveSlackSettings(apiFunction, widgetId) {
                if (!$scope.canUseSlackIntegration() || !$scope.model.forwardingSettings.slack.isConnected) {
                    return;
                }

                return apiFunction($stateParams.siteId, 'poll', widgetId, {
                    enabled: $scope.model.forwardingSettings.slack.enabled,
                    channels: $scope.model.forwardingSettings.slack.channels,
                    settings: {
                        questions: $scope.model.forwardingSettings.slack.selectedQuestions
                    }
                });
            }

            function saveMsTeamsSettings(surveyId) {
                return $scope.canUseMsTeamsIntegration().then((canUse) => {
                    if (!canUse) {
                        return;
                    }

                    return saveMsTeamsSurveySettings($stateParams.siteId, surveyId, {
                        enabled: $scope.model.forwardingSettings.msTeams.enabled,
                        settings: {
                            questions: $scope.model.forwardingSettings.msTeams.selectedQuestions || []
                        },
                        webhooks: $scope.model.forwardingSettings.msTeams.webhooks || []
                    });
                });
            }

            function saveWebhooksSettings(surveyId) {
                return $scope.canUseWebhooksIntegration().then((canUse) => {
                    if (!canUse) {
                        return;
                    }

                    const urls = $scope.model.forwardingSettings.webhooks.webhooks || [];

                    return saveWebhooksSurveySettings($stateParams.siteId, surveyId, {
                        enabled: $scope.model.forwardingSettings.webhooks.enabled,
                        webhooks: urls
                    });
                });
            }

            async function handleForwardingSettingsSave({ isEdit, widgetId = null }) {
                try {
                    const slackSavePromise = isEdit
                        ? saveSlackSettings(updateWidgetConfiguration, widgetId)
                        : saveSlackSettings(setWidgetConfiguration, widgetId);
                    const msTeamsSavePromise = saveMsTeamsSettings(widgetId);
                    const webhooksSavePromise = saveWebhooksSettings(widgetId);

                    await Promise.all([slackSavePromise, msTeamsSavePromise, webhooksSavePromise]);
                    return { success: true };
                } catch (error) {
                    $scope.toggleSection(SECTIONS.forwarding);
                    return { success: false, error: 'Unable to save forwarding settings' };
                }
            }

            function loadPoll() {
                const copyText = ' - COPY';

                // Load an existing poll.
                if ($stateParams.pollId) {
                    rPoll = rSite.one('polls', $stateParams.pollId);

                    rPoll.get().then(
                        function (poll) {
                            $scope.model.currentPoll = PollModel.create(poll);
                            initEmailForwardingForExistingPoll($scope.model.currentPoll);
                            initIntegrationForwardingForExistingPoll($scope.model.currentPoll);
                            applyEditCopyChanges();
                            adjustSections();
                            assureDefaultButtonColor();
                            initAdditionalColors();
                            onPollModelLoad();
                        },
                        function (res) {
                            restDefaultHandlers.onError(res);
                        }
                    );
                }

                // Populate a new poll copying data from another poll with the following changes:
                if ($stateParams.copyId) {
                    rPoll = rSite.one('polls', $stateParams.copyId);

                    rPoll.get().then(
                        function (poll) {
                            delete poll.uuid;
                            $scope.model.currentPoll = PollModel.create(poll);

                            initEmailForwardingForExistingPoll(poll);
                            initIntegrationForwardingForExistingPoll($scope.model.currentPoll);

                            // Make sure the name is not longer than 50 characters (limit).
                            if ($scope.model.currentPoll.name.length >= 50 - copyText.length) {
                                $scope.model.currentPoll.name = $scope.model.currentPoll.name.substr(
                                    0,
                                    50 - copyText.length
                                );
                            }

                            $scope.model.currentPoll.name += copyText;
                            $scope.model.currentPoll.enabled = false;
                            $scope.model.currentPoll.response_count = 0;
                            applyEditCopyChanges();
                            adjustSections();
                            onPollModelLoad();
                            $scope.form.$dirty = true;
                        },
                        function (res) {
                            restDefaultHandlers.onError(res);
                        }
                    );
                }

                // Populate a new poll with data from template.
                if ($scope.model.templateId) {
                    setTemplatePollData();
                }

                // Create a new poll.
                if (!$stateParams.pollId && !$stateParams.copyId && !$scope.model.templateId) {
                    createNewPoll();
                }

                // Show any UI Markers on this state.
                UiHelper.initMarkers();
            }

            function createNewPoll() {
                setNewPollData();
                onPollModelLoad();
                Track.browse('Polls Create');
            }

            /**
             * Set the data for a new Poll from template.
             */
            async function setTemplatePollData() {
                const poll = $scope.model.templateData;

                if (!poll) {
                    return createNewPoll();
                }

                $scope.model.currentPoll = createSurveyFromTemplate(poll);

                initForwardingForNewPoll();
                initEmailForwardingForExistingPoll(poll);
                initIntegrationForwardingForExistingPoll($scope.model.currentPoll);
                $scope.setAccessibleFontSkinColor();
                applyEditCopyChanges();
                adjustSections();
                onPollModelLoad();
                $scope.form.$dirty = true;
            }

            function createSurveyFromTemplate(template) {
                // set popover as a default display type for surveys created with templates
                // clean up data specific to the template survey
                return PollModel.create({
                    ...template,
                    display_type: 'popover',
                    response_count: 0,
                    forwarding_email_address_list: [],
                    id: undefined,
                    uuid: undefined,
                    background: template.background || $scope.defaultBackgroundColor
                });
            }

            /**
             * Get template id from AppState and get template data from API.
             */
            function checkIfTemplateAvailable() {
                const numericSiteId = Number($stateParams.siteId);

                // handle template id from in-app templates gallery
                const searchParams = new URLSearchParams(window.location.search);
                const inAppTemplateId = searchParams.get('templateId');
                const templateCategoryTitle = decodeURIComponent(searchParams.get('category') ?? '');
                if (inAppTemplateId) {
                    setSurveyTemplateIdToAppState(user.id, numericSiteId, null);
                    $scope.model.templateId = inAppTemplateId;
                    $scope.model.templateCategoryTitle = templateCategoryTitle;
                    return getSurveyTemplateById(inAppTemplateId).then((templateData) => {
                        $scope.model.templateData = templateData;
                    });
                }

                // handle template id from external templates gallery
                return getSurveyTemplateId(user.id, numericSiteId)
                    .then(({ templateId }) => {
                        if (templateId) {
                            setSurveyTemplateIdToAppState(user.id, numericSiteId, null);
                            $scope.model.templateId = templateId;
                            return getSurveyTemplateById($scope.model.templateId);
                        }
                        return null;
                    })
                    .then((templateData) => {
                        $scope.model.templateData = templateData;
                    });
            }

            /**
             * Set the data for a new Poll.
             */
            function setNewPollData() {
                const customPollData = NextItemData.get('poll');

                if (customPollData && customPollData.pollData) {
                    $scope.model.currentPoll = PollModel.create(customPollData.pollData);
                    NextItemData.clear('poll');
                    applyCustomDataChanges();
                } else {
                    const loadedSurvey = loadWizardState($stateParams.siteId);
                    if (loadedSurvey) {
                        $scope.model.currentPoll = PollModel.create({
                            ...defaultSurveyData,
                            ...loadedSurvey.data
                        });

                        initForwardingForNewPoll();
                        initIntegrationForwardingForExistingPoll($scope.model.currentPoll);
                        adjustSections();

                        Object.keys($scope.model.flowSections).every((sectionName) => {
                            $scope.updateSuccessStatus(sectionName);
                            if (sectionName === loadedSurvey.section) {
                                $scope.model.activeSection = sectionName;
                                return false;
                            }
                            return true;
                        });

                        scrollToSection(loadedSurvey.section);
                    } else {
                        $scope.model.currentPoll = PollModel.create(_.cloneDeep(defaultSurveyData));
                    }
                }

                initTargeting();
                initForwardingForNewPoll();
                initIntegrationForwardingForExistingPoll($scope.model.currentPoll);
                $timeout(() => {
                    canSave = true;
                }, 2000);
            }

            /**
             * Apply changes needed when the poll model is loaded.
             */
            function onPollModelLoad() {
                $scope.model.loaded = true;
            }

            function setFlowSectionOpened(flowSection) {
                const { name } = flowSection;
                $scope.model.activeSection = name;
                $scope.model.openedSections[name] = true;
                flowSection.success = true;
            }

            /**
             * Apply Edit/Copy changes needed when the poll model is loaded.
             * Note: Used when copying / editing.
             */
            function applyEditCopyChanges() {
                var poll = $scope.model.currentPoll,
                    openConditions = {
                        name: poll.name && poll.name.length > 0,
                        targeting: hasTargetingRules()
                    };

                initTargeting();

                var forwarding_email_address_list = $scope.model.currentPoll.forwarding_email_address_list;

                var isForwardingEmailAddressListEmpty =
                    !forwarding_email_address_list ||
                    // When feedback is created through widget email addresses are set to [].
                    !forwarding_email_address_list.length ||
                    // When feedback is created through page, first element is an empty string.
                    (forwarding_email_address_list.length === 1 && forwarding_email_address_list[0].length === 0);

                if (isForwardingEmailAddressListEmpty) {
                    $scope.model.sectionOptions.emailForwardingEnabled = false;
                } else {
                    $scope.model.forwarding_email_address_list = forwarding_email_address_list.join(', ');
                }

                $scope.model.currentPoll.is_custom_background_color_selected = isCustomBackground(
                    $scope.model.currentPoll.background
                );

                $scope.model.currentPoll.button_color ||= BUTTON_COLORS[0].fill;
                $scope.model.currentPoll.is_custom_button_color_selected = isCustomButtonColor(
                    $scope.model.currentPoll.button_color
                );

                _.each($scope.model.flowSections, function (item, key) {
                    // Only mark these sections open if certain criteria have been met.
                    if (Object.prototype.hasOwnProperty.call(openConditions, key) && !openConditions[key]) {
                        return;
                    }

                    setFlowSectionOpened($scope.model.flowSections[key]);
                });

                // When editing, open the review step unless some other is specified in URL
                const searchParams = new URLSearchParams(window.location.search);
                const sectionToOpen =
                    $scope.model.flowSections[searchParams.get('activeFlowSection')] ||
                    $scope.model.flowSections[$stateParams.activeFlowSection];
                if (sectionToOpen) {
                    $scope.model.activeSection = sectionToOpen.name;
                } else {
                    $scope.model.activeSection = 'review';
                }
            }

            /**
             * This poll is created as new, using custom data.
             */
            function applyCustomDataChanges() {
                applyEditCopyChanges();
                adjustSections();

                if ($scope.model.dataFromTemplate) {
                    $scope.model.activeSection = 'location';
                } else {
                    $scope.model.activeSection = 'appearance'; // Open the Apperance section.
                }
            }

            /**
             * Check if the poll has any targeting rules.
             */
            function hasTargetingRules() {
                return _.some($scope.model.currentPoll.targeting, (rule) => {
                    return rule.component === 'url' || rule.component === 'trigger';
                });
            }

            /**
             * Returns the current survey with image props adjusted to be sent to BE without editing the
             * original model, to keep the preview intact in case the call fails
             */
            function cleanupSurveyImages(currentSurvey) {
                if (!currentSurvey || !currentSurvey.content || !Array.isArray(currentSurvey.content.questions))
                    return currentSurvey;
                return {
                    ...currentSurvey,
                    content: {
                        ...currentSurvey.content,
                        questions: currentSurvey.content.questions.map(
                            ({ image, image_url, image_thumbnail_url, ...question }) => {
                                if (image) return { ...question, image };
                                return { ...question, image, image_url, image_thumbnail_url };
                            }
                        )
                    }
                };
            }

            async function savePoll() {
                // Validation.
                $scope.isSaving = true;
                const deferred = $q.defer();
                const areUserSettingsValid = new Promise((resolve) => {
                    setTimeout(() => {
                        resolve($scope.allVisibleSectionsValid());
                    }, 0);
                });

                // If the user settings (visible sections) are not valid, we have to return a promise manually.
                // Otherwise, just return the restangular call which returns a promise automatically after it has
                // completed.
                if (!(await areUserSettingsValid)) {
                    Notify.error('Save failed. Some details are invalid.');
                    $scope.isSaving = false;
                    deferred.reject();
                    return deferred.promise;
                }

                adjustHiddenSectionDataIfInvalid();

                const currentPoll = $scope.model.currentPoll;

                // Update model to remove duplicates.
                saveEmailForwardingSettings(currentPoll);

                const inEditMode = $scope.model.mode === 'edit';
                try {
                    let surveySaveResponse;

                    if (inEditMode) {
                        // Poll already exists, update it.
                        surveySaveResponse = await rSite
                            .one('polls', currentPoll.id)
                            .customPUT(cleanupSurveyImages(currentPoll));
                    } else {
                        currentPoll.created_from_template = $scope.model.templateId;
                        currentPoll.template_name = $scope.model.templateData?.name;
                        currentPoll.template_category = $scope.model.templateCategoryTitle;
                        Track.create('Poll');
                        gettingStartedGuide.markSurveyAsCompleted(UserData.currentSite, User.get().id);
                        // Poll didn't exist before, create it.
                        surveySaveResponse = await rPolls.post(cleanupSurveyImages(currentPoll), null);
                    }

                    if (!surveySaveResponse.success) {
                        return surveySaveResponse;
                    }

                    const forwardingSaveResponse = await handleForwardingSettingsSave({
                        isEdit: inEditMode,
                        widgetId: surveySaveResponse.poll_id || currentPoll.id
                    });

                    if (forwardingSaveResponse && forwardingSaveResponse.error) {
                        return forwardingSaveResponse;
                    }

                    return surveySaveResponse;
                } catch (error) {
                    restDefaultHandlers.onError(error);
                    return error;
                } finally {
                    $scope.isSaving = false;
                    invalidateRecentSurveysCache();
                    invalidateSurveyWidgets(Number($scope.model.siteId));
                    invalidateFeedbackToolsCount(Number($scope.model.siteId), 'surveys');
                    if (inEditMode) invalidateSurveyQuestions(Number($scope.model.siteId), currentPoll.id);
                }
            }

            function getLastQuestionIndex() {
                return $scope.model.currentPoll.content.questions.length - 1;
            }

            /**
             * Focus on a new question we just added.
             *
             * @param index Index of question to focus on.
             */
            function focusOnNewQuestion(index = getLastQuestionIndex()) {
                highlightQuestion(index);
                elementFocus('#question-text-' + index);
            }

            /**
             * Highlight a specific question.
             *
             * @param index Index of the question to highlight.
             */
            function highlightQuestion(index) {
                var questionBox = $('#question-' + index);

                questionBox.addClass('question-highlight');
                $timeout(function () {
                    questionBox.removeClass('question-highlight');
                }, 1000);
            }

            /**
             * Set the form as dirty, call a callback, and the reload the survey preview.
             *
             * @param callback Callback function to execute in the middle.
             */
            function setDirtyAndReloadPreview(callback, step) {
                if ($scope.form) {
                    $scope.form.$dirty = true;
                }
                if (callback) {
                    callback();
                }
                $scope.reloadPreview(step);
            }

            /**
             * Validate name section.
             */
            function validateNameSection() {
                return $scope.model.currentPoll.name;
            }

            function validateLocationSection() {
                return typeof $scope.model.currentPoll.display_type !== 'undefined';
            }

            /**
             * Validate questions section.
             */
            function validateQuestionsSection() {
                return $scope.areQuestionsValid() && isShowLegalValid();
            }

            function isShowLegalValid() {
                return !($scope.model.currentPoll.showLegal && !$scope.hasLegalDetails());
            }

            /**
             * Validate appearance section.
             */
            function validateAppearanceSection() {
                return (
                    isSelectedColorValid($scope.model.currentPoll.background) &&
                    isSelectedColorValid($scope.model.currentPoll.button_color)
                );
            }

            const isSelectedColorValid = (color) => /(^#[0-9A-F]{6}$)|(^#[0-9A-F]{3}$)/i.test(color);

            /**
             * Validate invitation section.
             */
            function validateInvitationSection() {
                return (
                    typeof $scope.model.currentPoll.invite_enabled !== 'undefined' &&
                    $scope.model.currentPoll.invite !== undefined &&
                    $scope.model.currentPoll.invite.title !== '' &&
                    $scope.model.currentPoll.invite.description !== '' &&
                    $scope.model.currentPoll.invite.button !== '' &&
                    $scope.model.currentPoll.invite.close !== ''
                );
            }

            function validateTargetingSection() {
                return $scope.isDeviceTargetingValid && $scope.isPageTargetingValid;
            }

            function isDelayValid(delay) {
                return !isNaN(delay) && delay >= 0 && delay <= 600;
            }

            function isAbandonValid() {
                return deviceTargetingRulesString($scope).indexOf('desktop') !== -1;
            }

            /**
             * Set the display delay value to a specific value.
             */
            $scope.setDelay = function (value) {
                if ($scope.model.currentPoll) {
                    $scope.model.currentPoll.display_delay = value;
                }
            };

            /**
             * Validate behavior section.
             */
            function validateBehaviorSection() {
                if ($scope.model.currentPoll.display_condition === 'delay') {
                    return isDelayValid($scope.model.currentPoll.display_delay);
                }
                if ($scope.model.currentPoll.display_condition === 'abandon') {
                    return isAbandonValid();
                }
                return true;
            }

            /**
             * Validate forwarding section.
             */
            function validateForwardingSection() {
                const options = [
                    $scope.model.forwardingSettings.email,
                    $scope.model.forwardingSettings.slack,
                    $scope.model.forwardingSettings.msTeams,
                    $scope.model.forwardingSettings.webhooks
                ];

                // While we still waiting for the api responses, we mark the section as valid.
                if (!$scope.model.forwardingSettings || options.some((option) => !option)) {
                    return true;
                }

                // option can be considered valid in two cases, either disabled and valid.
                return options.every((option) => !option.enabled || option.valid);
            }

            /**
             * Validate page targeting rules (using the url component).
             *
             * @param updateRulesInModel Set to true if we want to update the targeting object
             *                           within the model. If false, a copy is used instead –
             *                           This is done so that the rules are not normalized
             *                           as the user is typing in fields.
             */
            function validatePageTargetingRules() {
                return $scope.isPageTargetingValid;
            }

            /**
             * Open the "next" section.
             *
             * @param sectionName Name of section we want to open next.
             */
            function openNextSection(sectionName) {
                // Only open the "next" section if it has never been opened before.
                if ($scope.model.activeSection !== sectionName && !$scope.model.openedSections[sectionName]) {
                    $scope.openSection(sectionName);
                }
            }

            /**
             * Scroll to a section.
             *
             * @sectionName Name of section to scroll to.
             */
            function scrollToSection(sectionName) {
                // We use timeout:0 so the positions are always taken at the end of the digest cycle.
                $timeout(function () {
                    document.querySelector('#section-' + sectionName)?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
            }

            /**
             * Make sure a section is open and scroll to it.
             *
             * @param element CSS identifier of element to focus on.
             */
            function elementFocus(element) {
                $timeout(function () {
                    $(element).click().focus();
                }, 0);
            }

            /**
             * Update the stick section heights by triggering a scroll event.
             * NOTE: We use timeout here so the UI is updated at the very end,
             * after React components have rerendered.
             */
            function updateStickySectionHeight() {
                $timeout(function () {
                    angular.element($window).scroll();
                }, 50);
            }

            function adjustSections(skipPreview = false) {
                const hiddenSections = {};
                const enabledOffsite = $scope.isOffsite();
                const inviteEnabled = $scope.model.currentPoll.invite_enabled;

                if (!enabledOffsite) hiddenSections.invitation = true;

                if (enabledOffsite && !inviteEnabled) hiddenSections.targeting = true;

                $scope.model.hiddenSections = hiddenSections;

                // // Reload preview to reflect the change
                // // This is useful when both location and questions sections are open
                if (skipPreview === true) return;
                $scope.reloadPreview();
            }

            function deviceTargetingRulesString(scope) {
                return scope
                    .deviceTargetingRules()
                    .map((d) => d.pattern)
                    .join(',');
            }

            function assureDefaultButtonColor() {
                if (!$scope.model.currentPoll.button_color) {
                    $scope.model.currentPoll.button_color = $scope.defaultButtonColor;
                }
            }

            function getAdditionalColor(backgroundColor, currentColors, legacyColors) {
                return !currentColors.some((c) => c.fill === backgroundColor) && legacyColors.includes(backgroundColor)
                    ? backgroundColor
                    : null;
            }

            function initAdditionalColors() {
                $scope.additionalBackgroundColor = getAdditionalColor(
                    $scope.model.currentPoll.background,
                    BACKGROUND_COLORS,
                    LEGACY_BACKGROUND_COLORS
                );
                $scope.additionalButtonColor = getAdditionalColor(
                    $scope.model.currentPoll.button_color,
                    BUTTON_COLORS,
                    LEGACY_BUTTON_COLORS
                );
            }

            function navigateToResponsesAndTriggerCreatedSurvey(surveyId) {
                if ($scope.model.mode === 'create') {
                    scheduleTrigger('created_survey');
                }

                ReactNavigation.navigate(SURVEYS.RESPONSES.path, {
                    siteId: UserData.currentSite.id,
                    surveyId: surveyId ?? $scope.model.currentPoll.id
                });
            }

            /*************************
             * SCOPE FUNCTIONS
             *************************/

            $scope.highlightReviewSection = () => {
                //$scope.$apply ensures that the section is expanded before the scroll is attempted
                $scope.$apply(() => {
                    $scope.openSection(SECTIONS.review);
                });

                const reviewSection = document.querySelector('#section-review');

                if (reviewSection === null) return;
                reviewSection.scrollIntoView(true);

                const keyframes = [
                    { boxShadow: `0 0 1px -5px ${tokens.color.primary_cta}` },
                    { boxShadow: `0 0 1px 5px ${tokens.color.primary_cta}`, offset: 0.5 },
                    { boxShadow: `0 0 1px -5px ${tokens.color.primary_cta}`, offset: 1 }
                ];

                const timings = { duration: 1000, iterations: 1 };

                reviewSection.animate(keyframes, timings);
            };

            $scope.getReviewSections = () => {
                return Object.fromEntries(
                    Object.entries($scope.model.flowSections).filter(
                        ([key]) => key !== SECTIONS.review && !$scope.model.hiddenSections[key]
                    )
                );
            };

            $scope.getDisplayTypeLabel = (displayType = 'button') => {
                return i18next.t(`feedback:survey.display-type.${displayType}`);
            };

            $scope.isModal = () => isModal($scope.model?.currentPoll?.display_type);
            $scope.isOffsite = () => isOffsite($scope.model?.currentPoll?.display_type);
            $scope.isPopover = () => isPopover($scope.model?.currentPoll?.display_type);

            $scope.setDisplayType = (displayType) => {
                const enabledOffsite = isOffsite(displayType);

                $scope.model.currentPoll.display_type = displayType;

                if (!enabledOffsite) {
                    // On-site surveys cannot be saved with the invitation enabled
                    $scope.model.currentPoll.invite_enabled = false;
                }

                adjustSections();

                // revalidate sections
                _.each(
                    $scope.model.flowSections,
                    (section) => $scope.model.openedSections[section.name] && $scope.updateSuccessStatus(section.name)
                );

                $scope.form.$dirty = true;

                if (!$scope.$$phase) {
                    $scope.$apply();
                }
            };

            $scope.setInviteEnabled = (inviteEnabled) => {
                $scope.model.currentPoll.invite_enabled = inviteEnabled;
                adjustSections();

                // revalidate sections
                _.each(
                    $scope.model.flowSections,
                    (section) => $scope.model.openedSections[section.name] && $scope.updateSuccessStatus(section.name)
                );
            };

            /**
             * Update the text strings in the Invite modal.
             *
             * @param text All the text strings in the modal.
             */
            $scope.updateInviteText = function (newInviteValues) {
                $scope.model.currentPoll.invite = newInviteValues;
            };

            function initTargeting() {
                if ($scope.model.currentPoll === null || $scope.model.currentPoll === undefined) {
                    $scope.onTargetingChange(undefined);
                    return;
                }
                $scope.onTargetingChange(
                    $scope.model.currentPoll.targeting.map((target) => ({
                        id: uuid(),
                        error: undefined,
                        target
                    }))
                );
            }

            const getPageTargetingMode = function () {
                const targetingRules = _.filter($scope.model.currentPoll.targeting, (rule) => {
                    return rule.component === 'url' || rule.component === 'trigger';
                });

                if (
                    targetingRules.length === 1 &&
                    targetingRules[0].match_operation === 'contains' &&
                    targetingRules[0].pattern === '/'
                ) {
                    return 'all';
                }

                if (targetingRules.length > 0) return 'specific';

                return undefined;
            };

            $scope.pageTargetingMode = undefined;

            const getUserTargetingMode = function () {
                const targetingRules = _.filter($scope.model.currentPoll.targeting, (rule) => {
                    return rule.component === 'attribute';
                });

                if (targetingRules.length === 0) {
                    return 'all';
                }

                if (targetingRules.length > 0) return 'specific';

                return undefined;
            };

            $scope.userTargetingMode = undefined;

            $scope.isPageTargetingValid = true;
            $scope.isDeviceTargetingValid = true;

            $scope.onTargetingChange = (targeting) => {
                $scope.model.currentPoll.targeting = targeting.map((entry) => entry.target);
                $scope.targetingEntries = targeting;
                $scope.pageTargetingMode = getPageTargetingMode();
                $scope.userTargetingMode = getUserTargetingMode();
                const pageTargetingEntries = $scope.targetingEntries.filter(
                    (entry) => entry.target.component === 'url' || entry.target.component === 'trigger'
                );
                $scope.isPageTargetingValid =
                    pageTargetingEntries.length > 0 &&
                    pageTargetingEntries.every((entry) => entry.error == undefined && entry.target.pattern.length > 0);
                const deviceTargetingEntries = $scope.targetingEntries.filter(
                    (entry) => entry.target.component === 'device'
                );
                $scope.isDeviceTargetingValid =
                    deviceTargetingEntries.length > 0 &&
                    deviceTargetingEntries.every((entry) => entry.error == undefined);

                validatePageTargetingRules(false);
            };

            $scope.targetingEntries = undefined;

            /**
             * Check if this poll is targeting all pages.
             */

            /**
             * Save the poll.
             */
            $scope.savePoll = async function (redirectAfterSave = true) {
                let failedWithTypeChangeError = false;

                const savePollCallback = async function (res) {
                    if (res.success) {
                        $scope.onExit();
                        $scope.form.$dirty = false;
                        Notify.success('Survey saved');

                        resetForwardingSettings();

                        if (!redirectAfterSave) {
                            return;
                        }

                        navigateToResponsesAndTriggerCreatedSurvey(res.poll_id);

                        return;
                    }

                    if (!failedWithTypeChangeError && res.error_type === 'PollDisallowedQuestionTypeChanges') {
                        failedWithTypeChangeError = true;
                        $scope.model.currentPoll.regenerateUuidForPollQuestions(res.question_uuids);

                        savePollCallback(await savePoll());
                    }

                    const errorMessage = res?.error || res?.data?.error;

                    Notify.error(
                        errorMessage && typeof errorMessage === 'string'
                            ? errorMessage
                            : 'Unexpected error encountered while saving. Please try again or contact support.'
                    );
                };

                savePollCallback(await savePoll());
            };

            /**
             * Returns true if a specific section validates properly.
             * NOTE: This function validates the section as well.
             *
             * @param sectionName Name of section to check.
             * @param calledFromTemplate If true, it means the function was called from a template.
             */
            $scope.isSectionValid = function (sectionName, calledFromTemplate) {
                var valid = false;

                if (!$scope.model.currentPoll) {
                    return;
                }

                switch (sectionName) {
                    case SECTIONS.name:
                        valid = validateNameSection();
                        break;
                    case SECTIONS.location:
                        valid = validateLocationSection();
                        break;
                    case SECTIONS.questions:
                        valid = validateQuestionsSection();
                        break;
                    case SECTIONS.appearance:
                        valid = validateAppearanceSection();
                        break;
                    case SECTIONS.invitation:
                        valid = validateInvitationSection();
                        break;
                    case SECTIONS.targeting:
                        valid = validateTargetingSection(!calledFromTemplate);
                        break;
                    case SECTIONS.behavior:
                        valid = validateBehaviorSection();
                        break;
                    case SECTIONS.forwarding:
                        valid = validateForwardingSection();
                        break;
                    case SECTIONS.sharing:
                        valid = true;
                        break;
                    case SECTIONS.review:
                        valid = true;
                        break;
                }

                return Boolean(valid);
            };

            $scope.isAbandonDisabled = () => !isAbandonValid();

            $scope.isThankYouMessageValid = () =>
                $scope.model?.currentPoll?.content?.thankyou && $scope.model.currentPoll.content.thankyou?.length > 0;

            /**
             * Returns true if all questions and thank you message are valid.
             */
            $scope.areQuestionsValid = () => {
                if ($scope.model?.currentPoll?.content?.questions !== undefined) {
                    return (
                        $scope.model.currentPoll.content.questions.every((question) => question.isValid) &&
                        $scope.isThankYouMessageValid()
                    );
                }
                return true;
            };

            /**
             * Validate a section and update it's success status.
             * @param sectionName Name of section to validate.
             */
            $scope.updateSuccessStatus = function (sectionName) {
                var section = $scope.model.flowSections[sectionName];

                if (!$scope.model.currentPoll) {
                    return;
                }

                section.success = $scope.isSectionValid(sectionName);
            };

            function adjustHiddenSectionDataIfInvalid() {
                const hiddenSections = _.filter(
                    $scope.model.flowSections,
                    (section) => $scope.model.hiddenSections[section.name]
                );
                hiddenSections.forEach((section) => {
                    if (section.validate && !section.validate()) {
                        // adjust data to match default data
                        section.fallbackToDefaultData && section.fallbackToDefaultData();
                    }
                });
            }

            /**
             * Return true if all visible to the user sections validate properly.
             */
            $scope.allVisibleSectionsValid = function () {
                if (!$scope.model.currentPoll) {
                    return;
                }

                return _.every(
                    $scope.model.flowSections,
                    (section) => $scope.model.hiddenSections[section.name] || !section.validate || !!section.validate()
                );
            };

            $scope.validationErrors = [];

            const getValidationErrors = function () {
                return Object.entries($scope.model.flowSections)
                    .filter(([key]) => key !== 'review' && !$scope.model.hiddenSections[key])
                    .filter((entry) => !$scope.isSectionValid(entry[0], true))
                    .map(([key, section]) => ({
                        text: section.invalidMessage,
                        onClick: () => {
                            $scope.openSection(key);
                            scrollToSection(key);
                            $scope.updateSuccessStatus(key);
                        }
                    }));
            };

            $scope.$watch(
                () => getValidationErrors(),
                () => {
                    $scope.validationErrors = getValidationErrors();
                },
                true
            );

            /**
             * Return a list which includes all page targeting rules.
             */
            $scope.pageTargetingRules = () => {
                if ($scope.model.currentPoll) {
                    return _.filter($scope.model.currentPoll.targeting, function (r) {
                        return r.component === 'url' || r.component === 'trigger';
                    });
                }
                return [];
            };

            /**
             * Return a list which includes all device targeting rules.
             * NOTE: Negative rules can only be 'url' component types.
             */
            $scope.deviceTargetingRules = function () {
                if ($scope.model.currentPoll) {
                    return _.filter($scope.model.currentPoll.targeting, function (r) {
                        return r.component === 'device';
                    });
                }
                return [];
            };

            /**
             * Return a list which includes all attribute targeting rules.
             */
            $scope.attributeTargetingRules = function () {
                if ($scope.model.currentPoll) {
                    return _.filter($scope.model.currentPoll.targeting, function (r) {
                        return r.component === 'attribute';
                    });
                }
            };

            /**
             * Toggle a section.
             *
             * @param sectionName Name of section to toggle.
             * @param nextSectionName Name of next section we would like to open.
             */
            $scope.toggleSection = function (sectionName, nextSectionName) {
                // Close any uiHelpers currently showing.
                UiHelper.hide();
                if (sectionName === $scope.model.activeSection) {
                    // Close and validate if it's currently open.
                    $scope.model.activeSection = '';
                    $scope.updateSuccessStatus(sectionName);

                    // If a next section is defined, open it.
                    if (nextSectionName) {
                        openNextSection(nextSectionName);
                    }

                    if (sectionName === SECTIONS.forwarding) {
                        setForwardingDirty();
                    }
                } else {
                    // Open it and close the others if it's currently closed.
                    _.each($scope.model.flowSections, function (sec, name) {
                        if (name === sectionName) {
                            $scope.openSection(name);
                        } else if ($scope.model.openedSections[name]) {
                            if (name === SECTIONS.forwarding) {
                                setForwardingDirty();
                            }
                            if (!(name === SECTIONS.review && $scope.model.mode === 'edit')) {
                                // Do not close the review section while editing.
                                $scope.updateSuccessStatus(name);
                            }
                        }
                    });
                }

                scrollToSection(sectionName);
            };

            /**
             * Open a section.
             *
             * @param sectionName Name of section we want to open.
             */
            $scope.openSection = function (sectionName) {
                $scope.model.activeSection = sectionName;
                $scope.model.openedSections[sectionName] = true;

                // Focus on the input field if we opened the name section.
                if (sectionName === SECTIONS.name) {
                    elementFocus('#name');
                }

                // Tag the recording if the user can see the nifty new user attribute targeting.
                if (sectionName === SECTIONS.targeting) {
                    Track.userAttributesVisible();
                }

                // Focus on the first question if we opened the questions section.
                if (sectionName === SECTIONS.questions) {
                    $timeout(function () {
                        angular.element($window).scroll();
                        elementFocus('#question-text-0');
                    }, 0);
                }

                // Reset the preview if we opened the appearance section.
                if (sectionName === SECTIONS.appearance) {
                    $scope.reloadPreview();
                }

                // Load next steps if the location step is opened in create mode for the first time
                if (sectionName === SECTIONS.location && !$scope.model.flowSections.location.success) {
                    $timeout(function () {
                        $scope.setDisplayType($scope.model.currentPoll.display_type);
                    }, 0);
                }
            };

            const navigationMap = new Map([
                ['name', 'name'],
                ['targeting-section', 'targeting'],
                ['page-targeting', 'targeting'],
                ['device-targeting', 'targeting'],
                ['question', 'questions'],
                ['questions-section', 'questions'],
                ['appearance-section', 'appearance'],
                ['location-section', 'location']
            ]);

            $scope.onNavigateClicked = (navigation) => {
                const section = navigationMap.get(navigation.location);
                if (section === undefined) return;
                $scope.openSection(section);
                scrollToSection(section);
            };

            $scope.setAccessibleFontSkinColor = () => {
                $scope.model.currentPoll.skin = ColorHelper.isDarkOrLight($scope.model.currentPoll.background);
                $scope.previewSkin = null;
            };

            $scope.onBackgroundChange = ({ color, isCustom }) => {
                $scope.model.currentPoll.background = color;
                $scope.previewBackgroundColor = null;
                $scope.model.currentPoll.is_custom_background_color_selected = isCustom;

                $scope.setAccessibleFontSkinColor();

                if (color === BACKGROUND_COLORS[1].fill) {
                    $scope.onButtonColorChange({ color: BUTTON_COLORS[1].fill, isCustom: false });
                } else {
                    setDirtyAndReloadPreview();
                }
            };

            $scope.previewBackgroundColor = null;
            $scope.onPreviewBackgroundColor = (color) => {
                $scope.previewBackgroundColor = color;
                $scope.previewSkin = color ? ColorHelper.isDarkOrLight(color) : null;

                $scope.reloadPreview();
            };

            $scope.onButtonColorChange = ({ color, isCustom }) => {
                $scope.model.currentPoll.button_color = color;
                $scope.previewButtonColor = null;
                $scope.model.currentPoll.is_custom_button_color_selected = isCustom;

                setDirtyAndReloadPreview(null, 'buttonColor');
            };

            $scope.previewButtonColor = null;
            $scope.onPreviewButtonColor = (color) => {
                $scope.previewButtonColor = color;

                $scope.reloadPreview($scope.previewButtonColor ? 'buttonColor' : undefined);
            };

            const getSkinForColor = (color) => (color === FONT_COLORS[0].fill ? 'light' : 'dark');

            $scope.onFontColorChange = ({ color }) => {
                $scope.model.currentPoll.skin = getSkinForColor(color);
                $scope.previewSkin = null;

                setDirtyAndReloadPreview();
            };

            $scope.previewSkin = null;
            $scope.onPreviewFontColor = (color) => {
                $scope.previewSkin = color === null ? null : getSkinForColor(color);

                $scope.reloadPreview();
            };

            $scope.previewReaction = null;
            $scope.onPreviewReactionStyle = (previewReactionConfig) => {
                $scope.previewReaction = previewReactionConfig;

                $scope.reloadPreview(previewReactionConfig ? `question:${previewReactionConfig.questionId}` : null);
            };

            function getFontColorForSkin(skin) {
                if (skin === 'light') return FONT_COLORS[0].fill;
                return FONT_COLORS[1].fill;
            }

            $scope.getFontColor = () => {
                return getFontColorForSkin($scope.model?.currentPoll?.skin);
            };

            /**
             * Toggle the Hotjar branding on the poll.
             */
            $scope.toggleBranding = function (hideBranding) {
                $scope.model.currentPoll.show_branding = !hideBranding;
                setDirtyAndReloadPreview();
            };

            /**
             * Called whenever the logo image changes.
             *
             * @param base64Image
             */
            $scope.onChangeLogo = function (base64Image) {
                $scope.form.$dirty = true;
                rSite
                    .one('surveys', 0)
                    .all('image')
                    .customPOST({
                        action: 'preview_logo',
                        image: base64Image
                    })
                    .then(
                        function (res) {
                            if (res.success) {
                                $scope.model.currentPoll.logo = res.image;
                                $scope.logo_error_message = null;
                                setDirtyAndReloadPreview();
                            } else {
                                $scope.logo_error_message = res.error;
                            }
                        },
                        function (res) {
                            restDefaultHandlers.onError(res);
                        }
                    );
            };

            /**
             * Remove an existing logo from the survey.
             */
            $scope.removeLogo = function () {
                $scope.form.$dirty = true;
                $scope.model.currentPoll.logo = null;
                $scope.model.currentPoll.logo_url = null;
                $scope.logo_error_message = null;
                setDirtyAndReloadPreview();
            };

            /**
             * Toggle the Email forwarding field.
             */
            $scope.toggleEmailForwarding = function () {
                if (!$scope.model.sectionOptions.emailForwardingEnabled) {
                    $scope.model.forwarding_email_address_list = null;
                }
            };

            $scope.onAddNewQuestion = function (questionType, index) {
                $scope.addNewQuestion(questionType, index);
            };

            $scope.onAddQuestionFromQuestionBank = function (questionBankQuestion, index) {
                // An index of -1 means it should be placed at end of list.
                if (index == -1) {
                    index = $scope.model.currentPoll.content.questions.length;
                }
                $scope.model.currentPoll.addQuestion(questionBankQuestion, index);
                Track.addQuestionFromBank();
            };

            $scope.onRemoveQuestion = function (question) {
                $scope.removeQuestion(question);
            };

            $scope.onChangeQuestionOrder = function (questions) {
                $scope.model.currentPoll.content.questions = questions;
            };

            $scope.onThankYouMessageChange = function (message) {
                $scope.model.currentPoll.content.thankyou = message;
            };

            /**
             * Add a question to the Poll.
             *
             * @param questionType Question type object.
             * @param index Index of where the new question should be added.
             */
            $scope.addNewQuestion = function (questionType, index) {
                switch (questionType) {
                    case QUESTION_TYPES.singleOpenEndedMultipleLine:
                    case QUESTION_TYPES.singleOpenEndedSingleLine:
                        $scope.model.currentPoll.addOpenEndedQuestion(questionType, index);
                        break;

                    case QUESTION_TYPES.email:
                        $scope.model.currentPoll.addEmailQuestion(index);
                        break;

                    case QUESTION_TYPES.singleCloseEnded:
                    case QUESTION_TYPES.multipleCloseEnded:
                        $scope.model.currentPoll.addCloseEndedQuestion(
                            questionType,
                            [
                                { text: '', comments: false },
                                { text: '', comments: false }
                            ],
                            index
                        );
                        break;
                    case QUESTION_TYPES.yesNo:
                        $scope.model.currentPoll.addCloseEndedQuestion(
                            QUESTION_TYPES.singleCloseEnded,
                            [
                                { text: 'Yes', comments: false },
                                { text: 'No', comments: false }
                            ],
                            index
                        );
                        break;

                    case QUESTION_TYPES.ratingScale5:
                        $scope.model.currentPoll.addScaleQuestion(questionType, 5, index);
                        break;

                    case QUESTION_TYPES.ratingScale7:
                        $scope.model.currentPoll.addScaleQuestion(questionType, 7, index);
                        break;

                    case QUESTION_TYPES.netPromoterScore:
                        $scope.model.currentPoll.addNPSQuestion(index);
                        break;

                    case QUESTION_TYPES.titleAndDescription:
                        $scope.model.currentPoll.addTitleAndDescriptonQuestion(index);
                        break;

                    case QUESTION_TYPES.reaction:
                        $scope.model.currentPoll.addReactionQuestion(index);
                        break;

                    default:
                        throw new Error('$scope.addNewQuestion(): Question type ' + questionType + ' not allowed.');
                }

                addForwardingQuestion(index);
            };

            /**
             * Remove a question from the current poll using a confirmation prompt if needed.
             *
             * @param question Object to remove.
             * @param index Index of the question we are removing.
             */
            $scope.removeQuestion = function (question) {
                setDirtyAndReloadPreview(function () {
                    $scope.model.currentPoll.removeQuestionWithConfirmation(question);
                });

                removeForwardingQuestions(question);
            };

            $scope.updateQuestion = function (updatedQuestion, questionId, isValid) {
                const question = $scope.model.currentPoll.content.questions.find(
                    (question) => question.uuid === questionId
                );

                if (!question) return;

                setDirtyAndReloadPreview(() => {
                    _.extend(question, { ...updatedQuestion, isValid });
                    // This is needed so that our React component understands that there is a change
                    // in the poll data
                    $scope.model.currentPoll.content.questions = [...$scope.model.currentPoll.content.questions];
                }, `question:${question.uuid}`);

                updateStickySectionHeight();
                updateForwardingQuestions(question);
            };

            $scope.updateConnectVisitData = function (connectVisitData) {
                setDirtyAndReloadPreview(() => {
                    _.extend($scope.model.currentPoll, {
                        connect_visit_data: connectVisitData
                    });
                }, 'thankYou');
            };

            $scope.enabledAndValid = ({ enabled, valid } = {}) => enabled && valid;

            $scope.onForwardChanges = function (forwardingSettings) {
                // NOTE: Recreate the object here to trigger re-render of the root React component
                // so questions form can appear when Slack or Microsoft Teams are enabled
                if (forwardingSettings.slack) {
                    $scope.model.forwardingSettings.slack = {
                        ...$scope.model.forwardingSettings.slack,
                        ...forwardingSettings.slack
                    };
                } else if (forwardingSettings.msTeams) {
                    $scope.model.forwardingSettings.msTeams = {
                        ...$scope.model.forwardingSettings.msTeams,
                        ...forwardingSettings.msTeams
                    };
                } else if (forwardingSettings.webhooks) {
                    $scope.model.forwardingSettings.webhooks = {
                        ...$scope.model.forwardingSettings.webhooks,
                        ...forwardingSettings.webhooks
                    };
                } else if (forwardingSettings.email) {
                    Object.assign($scope.model.forwardingSettings.email, forwardingSettings.email);
                }
            };

            $scope.onSlackForwardingQuestionChanges = function (questions) {
                $timeout(() => {
                    $scope.model.forwardingSettings.slack.selectedQuestions = questions;
                });
            };

            $scope.onMsTeamsForwardingQuestionChanges = function (questions) {
                $timeout(() => {
                    $scope.model.forwardingSettings.msTeams.selectedQuestions = questions;
                });
            };

            $scope.hasPollResponses = function () {
                return $scope.model?.currentPoll?.response_count && $scope.model.currentPoll.response_count > 0;
            };

            /**
             * Reset the question bank filter.
             */
            $scope.resetBankFilter = function () {
                $scope.model.sectionOptions.bankFilter = '';
            };

            /**
             * Return true if the language selected is RTL.
             */
            $scope.isRtlLanguage = function () {
                if ($scope.model.currentPoll) {
                    return $scope.model.rtlLanguages.indexOf($scope.model.currentPoll.language) > -1;
                }
            };

            /**
             * Set the active tab in the Questions section.
             */
            $scope.setActiveQuestionTab = function (tab) {
                $scope.model.sectionOptions.activeQuestionTab = tab;

                if (tab === 'bank') {
                    Track.click('survey_create_question_bank_tab', 'Survey create/edit Questions section', {});
                }
            };

            /**
             * Set the display condition.
             *
             * @param display_condition One of 'immediate', 'delay', 'abandon' or 'scroll'.
             */
            $scope.setDisplayCondition = function (display_condition) {
                $scope.model.currentPoll.display_condition = display_condition;
            };

            /**
             * Set the persist condition.
             *
             * @param persist_condition One of 'response', 'once', or 'always'.
             */
            $scope.setPersistCondition = function (persist_condition) {
                $scope.model.currentPoll.persist_condition = persist_condition;
            };

            /**
             * Copy an existing poll to a new one.
             *
             * @param poll Poll object to copy.
             */
            $scope.copyPoll = function () {
                $state.go('surveys.create', { copyId: $scope.model.currentPoll.id });
            };

            /**
             * Show the 'poll responses' view which contains the outcome of the poll.
             */
            $scope.showPollResponses = function () {
                ReactNavigation.navigate(SURVEYS.RESPONSES.path, {
                    siteId: UserData.currentSite.id,
                    surveyId: $scope.model.currentPoll.id
                });
            };

            $scope.onShowLegalChange = function () {
                $scope.model.currentPoll = {
                    ...$scope.model.currentPoll,
                    show_legal: !$scope.model.currentPoll.show_legal
                };
                $scope.reloadPreview('thankYou');
            };

            /**
             * Check is the site has the required legal details (legal name and privacy policy link)
             */
            $scope.siteHasLegalDetails = function () {
                return Site.hasLegalDetails();
            };

            $scope.showUpsellFromBranding = function () {
                $scope.showUpsellModal('polls.branding.toggle');
            };

            $scope.showUpsellFromTargetingCap = function () {
                $scope.showUpsellModal('polls.targeting.cap');
            };

            $scope.showColorUpsellModal = function () {
                $scope.showUpsellModal('survey.appearance.colors');
            };

            // Every time the questions are sorted, reload the preview.
            $rootScope.$on('sortableListUpdated', $scope.reloadPreview);

            $rootScope.$on('addedQuestion', function (_, args) {
                $timeout(function () {
                    // Reload the preview.
                    setDirtyAndReloadPreview();

                    // Focus on the new question.
                    focusOnNewQuestion(args.index);
                }, 0);

                // Update the sticky section height whenever we add a question.
                updateStickySectionHeight();
            });

            function applyPreviewOverrides(config) {
                config.background = $scope.previewBackgroundColor ?? config.background;
                config.buttonColor = $scope.previewButtonColor ?? config.buttonColor;
                config.previewSkin = $scope.previewSkin ?? config.previewSkin;

                if ($scope.previewReaction && config.content) {
                    for (let i = 0; i < config.content.questions.length; i++) {
                        const question = config.content.questions[i];
                        if (question.uuid === $scope.previewReaction.questionId) {
                            config.content.questions[i] = {
                                ...question,
                                reaction_style: $scope.previewReaction.style
                            };
                            break;
                        }
                    }
                }

                return config;
            }

            $scope.reloadPreview = (step) => {
                $scope.model.previewConfig = applyPreviewOverrides(
                    cloneDeep({
                        activeStepInPreview: step,
                        background: $scope.model.currentPoll.background,
                        buttonColor: $scope.model.currentPoll.button_color,
                        connectVisitData: $scope.model.currentPoll.connect_visit_data,
                        content: $scope.model.currentPoll.content,
                        displayType: $scope.model.currentPoll.display_type,
                        language: $scope.model.currentPoll.language,
                        legalName: UserData.currentSite.legal_name,
                        logo: $scope.model.currentPoll.logo,
                        logoUrl: $scope.model.currentPoll.logo_url,
                        position: $scope.model.currentPoll.position,
                        privacyPolicyUrl: UserData.currentSite.privacy_policy_url,
                        showBranding: $scope.model.currentPoll.show_branding,
                        showLegal: $scope.model.currentPoll.show_legal,
                        skin: $scope.model.currentPoll.skin
                    })
                );

                reloadForwardingQuestions();
            };

            $rootScope.$on('removedQuestion', function () {
                // To prevent the preview from showing an empty state,
                // always show the first question when deleting a question.
                const firstQuestion = $scope.model.currentPoll.content.questions[0];
                if (firstQuestion) $scope.reloadPreview(`question:${firstQuestion.id}`);

                // Update the sticky section height whenever we remove a question.
                updateStickySectionHeight();
            });

            function isInvalidDisplayCondition(newTargetingString, oldTargetingString) {
                return (
                    newTargetingString.indexOf('desktop') === -1 &&
                    oldTargetingString.indexOf('desktop') !== -1 &&
                    $scope.model.currentPoll.display_condition === 'abandon'
                );
            }

            $scope.$watch(deviceTargetingRulesString, (newTargetingString, oldTargetingString) => {
                if (isInvalidDisplayCondition(newTargetingString, oldTargetingString)) {
                    $scope.model.currentPoll.display_condition = 'immediate';
                }
            });

            $scope.setName = function (newName) {
                $scope.model.currentPoll.name = newName;
                $scope.$digest();
            };

            $scope.setDescription = function (newDescription) {
                $scope.model.currentPoll.description = newDescription;
                $scope.$digest();
            };

            $scope.onPercentageChanged = (percentage) => {
                $scope.model.currentPoll.targeting_percentage = percentage;
            };

            $scope.onLanguageChanged = (newLanguage) => {
                $scope.$apply(function () {
                    $scope.model.currentPoll.language = newLanguage;
                    $scope.reloadPreview();
                });
            };

            $scope.onPositionChanged = (newPosition) => {
                $scope.$apply(function () {
                    $scope.model.currentPoll.position = newPosition;
                    $scope.reloadPreview();
                });
            };

            $scope.onStatusChanged = (status) => {
                $scope.model.currentPoll.enabled = status;
            };

            saveInterval = $interval(function () {
                if (/\d\/surveys\/create.*/.test(window.location.href)) {
                    if (initialized) {
                        clearWizardState($stateParams.siteId);
                        $state.reload();
                        initialized = false;
                    } else if (canSave) {
                        const openSection = Object.entries($scope.model.flowSections).find(
                            ([, { name }]) => name === $scope.model.activeSection
                        );
                        const openSectionName = openSection && openSection[0];
                        saveWizardState($scope.model.currentPoll, $stateParams.siteId, openSectionName);
                    }
                }
            }, 200);

            $scope.onExit = () => {
                clearWizardState($stateParams.siteId);
                initialized = true;
                canSave = false;
            };

            $scope.$on('$destroy', function () {
                $interval.cancel(saveInterval);
                saveInterval = undefined;
            });

            $scope.setOpenedSections = (sectionName) => {
                $scope.model.openedSections[sectionName] = true;
            };
        }
    );
